/**
 * Timezone-aware day boundaries.
 *
 * Daily new-card limits need a notion of "today" in the *learner's* zone. Using
 * UTC would reset the counter at 03:30 in Tehran, mid-evening in New York —
 * splitting one study session across two "days" and letting the cap be exceeded.
 */

/** The user's zone as reported by the browser; a sane default for `settings.tz`. */
export function guessTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

/**
 * How far `tz` is from UTC at a given instant, in ms (east of UTC is positive).
 * Derived by formatting the instant into `tz` and re-reading those wall-clock
 * parts as if they were UTC — the difference is the offset. This is the standard
 * trick for doing zone maths with only `Intl` available.
 */
function offsetMs(tz: string, at: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(at);

  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);

  const asIfUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    // Intl can render midnight as hour "24" in some locales/zones.
    get("hour") % 24,
    get("minute"),
    get("second"),
  );
  return asIfUtc - Math.floor(at.getTime() / 1000) * 1000;
}

/**
 * The instant at which the current day began in `tz`.
 *
 * Two-pass: guess by treating the local calendar date as UTC midnight, then
 * correct by the offset in effect at that guess. A single correction is exact
 * except when a DST transition falls within the corrected interval — no longer
 * a concern for Iran (DST abolished in 2022), and at worst an hour's skew in
 * deciding which day a review belongs to.
 */
export function startOfDay(tz: string, now: Date = new Date()): Date {
  const ymd = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now); // en-CA gives ISO-shaped "2026-07-31"

  const guess = new Date(`${ymd}T00:00:00Z`);
  return new Date(guess.getTime() - offsetMs(tz, guess));
}

/** Short "Jul 20" style date, for "Started {when}" on a collection card. */
export function formatShortDate(date: Date, lang: "en" | "fa"): string {
  return new Intl.DateTimeFormat(lang === "fa" ? "fa-IR" : "en-US", {
    month: "short",
    day: "numeric",
  }).format(date);
}

/** Human-readable "in 3 days" / "in 2 months" for the next-review hint. */
export function formatInterval(from: Date, to: Date, lang: "en" | "fa"): string {
  const mins = Math.round((to.getTime() - from.getTime()) / 60000);
  const rtf = new Intl.RelativeTimeFormat(lang === "fa" ? "fa" : "en", { numeric: "auto" });

  if (mins < 1) return lang === "fa" ? "همین حالا" : "now";
  if (mins < 60) return rtf.format(mins, "minute");
  if (mins < 60 * 24) return rtf.format(Math.round(mins / 60), "hour");

  const days = Math.round(mins / (60 * 24));
  if (days < 31) return rtf.format(days, "day");
  if (days < 365) return rtf.format(Math.round(days / 30), "month");
  return rtf.format(Math.round(days / 365), "year");
}
